import { err, ok, type Result } from '@nx-paeonia/utils-result';
import { captureException } from '@sentry/browser';

export type EventError = { type: 'ElementNotFound'; selector: string } | { type: 'HandlerError'; message: string };

export type SafeEventOptions = {
  once?: boolean;
  passive?: boolean;
  capture?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
};

export const safeEventListener = <K extends keyof HTMLElementEventMap, ElementTarget extends HTMLElement = HTMLElement>(
  selector: string,
  eventType: K,
  handler: (event: HTMLElementEventMap[K], element: ElementTarget) => void | Promise<void>,
  options?: SafeEventOptions,
): Result<() => void, EventError> => {
  const elements = document.querySelectorAll<HTMLElement>(selector);

  if (elements.length === 0) {
    return err({ type: 'ElementNotFound', selector });
  }

  const wrappedHandler = async (event: HTMLElementEventMap[K]) => {
    try {
      if (options?.preventDefault) event.preventDefault();
      if (options?.stopPropagation) event.stopPropagation();

      const element = event.currentTarget as ElementTarget; // We know this
      await handler(event, element);
    } catch (error) {
      captureException(error);
    }
  };

  const removeListeners: (() => void)[] = [];
  elements.forEach((element) => {
    element.addEventListener(eventType, wrappedHandler, {
      once: options?.once,
      passive: options?.passive,
      capture: options?.capture,
    });

    removeListeners.push(() => {
      element.removeEventListener(eventType, wrappedHandler, {
        capture: options?.capture,
      });
    });
  });

  const cleanup = () =>
    removeListeners.forEach((remove) => {
      remove();
    });

  return ok(cleanup);
};

// Convenience functions for common events
export const onClick = (
  selector: string,
  handler: (event: MouseEvent, element: HTMLElement) => void | Promise<void>,
  options?: SafeEventOptions,
) => safeEventListener(selector, 'click', handler, options);

export const onClick$ = <ElementTarget extends HTMLElement>(
  subSelector: string,
  handler: (event: MouseEvent, element: ElementTarget) => void | Promise<void>,
  options?: SafeEventOptions,
) =>
  safeEventListener(
    'body',
    'click',
    (event: MouseEvent) => {
      const target = event.target as ElementTarget;
      if (target.matches(subSelector)) {
        if (options?.preventDefault) event.preventDefault();
        if (options?.stopPropagation) event.stopPropagation();

        handler(event, target);
      }
    },
    {
      capture: options?.capture,
      once: options?.once,
      passive: options?.passive,
    },
  );

export const onSubmit = (
  selector: string,
  handler: (event: SubmitEvent, element: HTMLElement) => void | Promise<void>,
  options?: SafeEventOptions,
) =>
  safeEventListener(selector, 'submit', handler, {
    preventDefault: true,
    ...options,
  });

export const onChange = (
  selector: string,
  handler: (event: Event, element: HTMLElement) => void | Promise<void>,
  options?: SafeEventOptions,
) => safeEventListener(selector, 'change', handler, options);

export const onChange$ = (
  subSelector: string,
  handler: (event: Event, element: HTMLElement) => void | Promise<void>,
  options?: SafeEventOptions,
) =>
  safeEventListener(
    'body',
    'change',
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.matches(subSelector)) {
        if (options?.preventDefault) event.preventDefault();
        if (options?.stopPropagation) event.stopPropagation();
        handler(event, target);
      }
    },
    {
      capture: options?.capture,
      once: options?.once,
      passive: options?.passive,
    },
  );

export const onEvent$ = (
  subSelector: string,
  event: keyof HTMLElementEventMap,
  handler: (event: Event, element: HTMLElement) => void | Promise<void>,
  options?: SafeEventOptions,
) =>
  safeEventListener(
    'body',
    event,
    (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.matches(subSelector)) {
        if (options?.preventDefault) event.preventDefault();
        if (options?.stopPropagation) event.stopPropagation();
        handler(event, target);
      }
    },
    { capture: options?.capture, once: options?.once, passive: options?.passive },
  );

export const onInput = <ElementTarget extends HTMLElement>(
  selector: string,
  handler: (event: InputEventInit, element: ElementTarget) => void | Promise<void>,
  options?: SafeEventOptions,
) => safeEventListener(selector, 'input', handler, options);

// Batch event listener management
export class EventManager {
  private cleanupFunctions: (() => void)[] = [];

  add<K extends keyof HTMLElementEventMap>(
    selector: string,
    eventType: K,
    handler: (event: HTMLElementEventMap[K], element: HTMLElement) => void | Promise<void>,
    options?: SafeEventOptions,
  ): Result<void, EventError> {
    const result = safeEventListener(selector, eventType, handler, options);
    if (result.success) {
      this.cleanupFunctions.push(result.data);
      return ok(undefined);
    }
    return result;
  }

  removeAll(): void {
    this.cleanupFunctions.forEach((cleanup) => {
      cleanup();
    });
    this.cleanupFunctions = [];
  }
}
